<script setup lang="ts">
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'
import { reactive } from 'vue'

const toast = useToast();
const router = useRouter()
const userSchema = z.object({
  firstName: z.string().nonempty("Le prénom est requis"),
  lastName: z.string().nonempty("Le nom est requis"),
  email: z.string()
    .optional()
    .nullable()
    .refine((val) => !val || (val.includes('@') && !val.includes(' ')), 'L\'email ne doit pas contenir d\'espaces et doit contenir un @'),
  phoneNumber: z.string()
    .optional()
    .refine((val) => !val || (!val.includes('.') && !val.includes(' ')), 'Le numéro de téléphone ne doit pas contenir de points ou d\'espaces'),
  dateOfBirth: z.string().nonempty("La date de naissance est requise"),
  gender: z.enum(['Homme', 'Femme', 'Autre'], {
    errorMap: () => ({ message: "Le genre est requis" })
  }),
  password: z.string()
      .min(8, "Le mot de passe doit contenir au moins 8 caractères")
      .regex(/[A-Z]/, "Le mot de passe doit contenir une majuscule")
      .regex(/[\W_]/, "Le mot de passe doit contenir un caractère spécial")
})

type Schema = z.output<typeof userSchema>

const state = reactive({
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  dateOfBirth: '',
  gender: '',
  password: ''
})

async function onSubmit(event: FormSubmitEvent<Schema>) {
  const validationResult = userSchema.safeParse(event.data);
  if (!validationResult.success) {
    toast.add({
      id: "validation-error",
      color: "red",
      icon: "i-heroicons-x-circle",
      title: "Erreur de validation",
      description: 'Veuillez corriger les erreurs dans le formulaire.',
    });
    return;
  }

  try {
    const response = await fetch('/api/users/createUser', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(validationResult.data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Une erreur est survenue');
    }
    
    await response.json();
    toast.add({
      id: "success",
      color: "green",
      icon: "i-heroicons-check-circle",
      title: `Bienvenue ${event.data.firstName} ${event.data.lastName} !`,
      description: "Compte créé avec succès",
    });
    router.push({ path: '/admin/dashboard' });
  } catch (error) {
    const { handleApiError } = useErrorHandler()
    handleApiError(error, 'inscription')
  }
}
</script>

<template>
  <div class="flex items-center justify-center min-h-screen bg-gray-100 px-4 pt-40 pb-20">
    <UCard class="w-full max-w-3xl bg-white rounded-lg shadow-md my-auto">
      <div class="space-y-8 md:space-y-12">
        <div class="pb-8 md:pb-12 border-b border-gray-900/10">
          <h2 class="leading-6 text-2xl md:text-3xl text-gray-900">Création du compte</h2>
          <p class="mt-1 text-gray-600 text-sm/6">Vous êtes à quelque click de pouvoir rejoindre la Charly's Family</p>
        </div>
      </div>
        <UForm :schema="userSchema" :state="state" class="space-y-4" @submit="onSubmit">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <UFormGroup label="Nom" name="lastName">
            <UInput v-model="state.lastName" />
          </UFormGroup>
          <UFormGroup label="Prénom" name="firstName">
            <UInput v-model="state.firstName" />
          </UFormGroup>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <UFormGroup label="Adresse Email" name="email">
            <UInput v-model="state.email" type="text" />
          </UFormGroup>
          <UFormGroup label="Numéro de téléphone" name="phoneNumber">
            <UInput v-model="state.phoneNumber" type="tel" />
          </UFormGroup>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <UFormGroup label="Date de naissance" name="dateOfBirth">
            <UInput v-model="state.dateOfBirth" type="date" />
          </UFormGroup>
          <UFormGroup label="Genre" name="gender">
            <USelect v-model="state.gender" :options="['Homme', 'Femme', 'Autre']" />
          </UFormGroup>
        </div>
        <UFormGroup label="Mot de passe" name="password">
          <p class="text-sm text-gray-600">Le mot de passe doit contenir :</p>
          <ul class="pl-5 text-sm text-gray-600 list-disc mb-4">
            <li>Au moins 8 caractères</li>
            <li>Une majuscule</li>
            <li>Un caractère spécial</li>
          </ul>
          <UInput v-model="state.password" type="password" />
        </UFormGroup>
        <div class="flex justify-end gap-2 pt-4">
          <UButton type="submit" color="green" class="w-full sm:w-auto">Création du compte</UButton>
        </div>
      </UForm>
    </UCard>
  </div>
</template>
